/*
 * /settings/appearance — page behavior.
 *
 * 1:1 port of metainventory's AppearancePage.tsx + usePreferences.ts:
 *   • mode + family chip filtering, system row visibility
 *   • theme card → window.setTheme() (defined in app.js)
 *   • accent override (curated swatches + custom hex) — derives
 *     hover/active/subtle/text-on-accent variants
 *   • display: density / motion / fontScale segmented controls
 *
 * Persistence:
 *   theme preference   → 'theme'                      (handled by app.js)
 *   display preferences → 'metaproject-preferences'   (this file)
 *
 * Side-effects applied to <html>: data-density, data-motion,
 * font-size, and inline accent-related custom properties.
 */
(function () {
    'use strict';

    /* ────────────────────────── Color math ───────────────────── */

    function hexToRgb(hex) {
        const cleaned = hex.replace('#', '');
        if (cleaned.length !== 3 && cleaned.length !== 6) return null;
        const full = cleaned.length === 3
            ? cleaned.split('').map((c) => c + c).join('')
            : cleaned;
        const num = parseInt(full, 16);
        if (Number.isNaN(num)) return null;
        return { r: (num >> 16) & 0xff, g: (num >> 8) & 0xff, b: num & 0xff };
    }

    function rgbToHsl(r, g, b) {
        const rN = r / 255, gN = g / 255, bN = b / 255;
        const max = Math.max(rN, gN, bN), min = Math.min(rN, gN, bN);
        const l = (max + min) / 2;
        let h = 0, s = 0;
        if (max !== min) {
            const d = max - min;
            s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
            switch (max) {
                case rN: h = (gN - bN) / d + (gN < bN ? 6 : 0); break;
                case gN: h = (bN - rN) / d + 2; break;
                case bN: h = (rN - gN) / d + 4; break;
            }
            h *= 60;
        }
        return { h, s: s * 100, l: l * 100 };
    }

    function hslToHex(h, s, l) {
        const sN = s / 100, lN = l / 100;
        const c = (1 - Math.abs(2 * lN - 1)) * sN;
        const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
        const m = lN - c / 2;
        let r = 0, g = 0, b = 0;
        if (h < 60)       { r = c; g = x; }
        else if (h < 120) { r = x; g = c; }
        else if (h < 180) { g = c; b = x; }
        else if (h < 240) { g = x; b = c; }
        else if (h < 300) { r = x; b = c; }
        else              { r = c; b = x; }
        const to255 = (v) => Math.round((v + m) * 255);
        const toHex = (v) => to255(v).toString(16).padStart(2, '0');
        return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
    }

    function deriveAccentVariants(hex) {
        const rgb = hexToRgb(hex);
        if (!rgb) return null;
        const hsl = rgbToHsl(rgb.r, rgb.g, rgb.b);
        const hoverL = Math.min(hsl.l + 8, 90);
        const activeL = Math.max(hsl.l - 8, 8);
        const luminance =
            0.2126 * (rgb.r / 255) + 0.7152 * (rgb.g / 255) + 0.0722 * (rgb.b / 255);
        const textOnAccent = luminance > 0.55 ? '#0A0A0A' : '#FFFFFF';
        return {
            base:         hex.toUpperCase(),
            hover:        hslToHex(hsl.h, hsl.s, hoverL).toUpperCase(),
            active:       hslToHex(hsl.h, hsl.s, activeL).toUpperCase(),
            subtle:       `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.12)`,
            textOnAccent: textOnAccent,
        };
    }

    // Expose for app.js / boot script reuse.
    window.deriveAccentVariants = deriveAccentVariants;

    /* ────────────────────────── Preferences ──────────────────── */

    const STORAGE_KEY = 'metaproject-preferences';
    const DEFAULTS = {
        accentOverride: null,
        density: null,
        motion: null,
        fontScale: null,
    };

    function readStored() {
        try {
            const raw = localStorage.getItem(STORAGE_KEY);
            if (!raw) return Object.assign({}, DEFAULTS);
            const p = JSON.parse(raw);
            return {
                accentOverride: typeof p.accentOverride === 'string' ? p.accentOverride : null,
                density:        p.density === 'compact' || p.density === 'comfortable' ? p.density : null,
                motion:         p.motion === 'reduced' || p.motion === 'full' ? p.motion : null,
                fontScale:
                    p.fontScale === 0.9 || p.fontScale === 1.1 ? p.fontScale :
                    p.fontScale === 1 || p.fontScale === 1.0 ? 1.0 : null,
            };
        } catch {
            return Object.assign({}, DEFAULTS);
        }
    }

    function persist(prefs) {
        try { localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs)); }
        catch { /* unavailable */ }
    }

    function applyPreferences(prefs) {
        const html = document.documentElement;

        if (prefs.accentOverride) {
            const v = deriveAccentVariants(prefs.accentOverride);
            if (v) {
                html.style.setProperty('--accent',         v.base);
                html.style.setProperty('--accent-hover',   v.hover);
                html.style.setProperty('--accent-active',  v.active);
                html.style.setProperty('--accent-subtle',  v.subtle);
                html.style.setProperty('--text-on-accent', v.textOnAccent);
                html.style.setProperty('--border-focus',   v.base);
            }
        } else {
            html.style.removeProperty('--accent');
            html.style.removeProperty('--accent-hover');
            html.style.removeProperty('--accent-active');
            html.style.removeProperty('--accent-subtle');
            html.style.removeProperty('--text-on-accent');
            html.style.removeProperty('--border-focus');
        }

        if (prefs.density)  html.setAttribute('data-density', prefs.density);
        else                html.removeAttribute('data-density');

        if (prefs.motion)   html.setAttribute('data-motion', prefs.motion);
        else                html.removeAttribute('data-motion');

        if (prefs.fontScale && prefs.fontScale !== 1.0) {
            html.style.fontSize = `${prefs.fontScale * 16}px`;
        } else {
            html.style.removeProperty('font-size');
        }
    }

    // Apply at load (covers pages without the appearance form).
    let preferences = readStored();
    applyPreferences(preferences);

    // Expose so other pages / scripts can apply at boot.
    window.metaprojectPreferences = {
        get: () => Object.assign({}, preferences),
        apply: applyPreferences,
        derive: deriveAccentVariants,
    };

    /* ────────────────────── DOM bindings ─────────────────────── */

    const cards   = document.querySelectorAll('.appearance-preview-card');
    if (!cards.length) return; // not on appearance page

    const modeChips    = document.querySelectorAll('[data-mode-chip]');
    const familyChips  = document.querySelectorAll('[data-family-chip]');
    const systemRow    = document.querySelector('[data-system-row]');
    const familyGroups = document.querySelectorAll('.appearance-family-group');
    const emptyState   = document.querySelector('[data-empty-state]');
    const resetTheme   = document.querySelector('[data-action="reset-theme"]');
    const accentSwatches = document.querySelectorAll('.appearance-accent-swatch');
    const hexInput     = document.getElementById('accent-hex-input');
    const applyBtn     = document.querySelector('[data-action="apply-hex"]');
    const accentDot    = document.querySelector('[data-accent-dot]');
    const accentPreview = document.querySelector('[data-accent-preview]');
    const resetAccent  = document.querySelector('[data-action="reset-accent"]');
    const segmenteds   = document.querySelectorAll('.appearance-segmented');
    const resetAll     = document.querySelector('[data-action="reset-all"]');

    /* ─── Theme selection ────────────────────────────────────── */

    function currentPreference() {
        return localStorage.getItem('theme') || 'system';
    }

    function syncCardSelection() {
        const pref = currentPreference();
        cards.forEach((c) => {
            c.setAttribute('aria-pressed', c.dataset.themeValue === pref ? 'true' : 'false');
        });
        if (resetTheme) {
            resetTheme.hidden = pref === 'system';
        }
    }

    cards.forEach((card) => {
        card.addEventListener('click', () => {
            const value = card.dataset.themeValue;
            if (!value) return;
            if (typeof window.setTheme === 'function') {
                window.setTheme(value);
            } else {
                localStorage.setItem('theme', value);
                document.documentElement.setAttribute('data-theme', value);
            }
            syncCardSelection();
        });
        card.addEventListener('keydown', (e) => {
            if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(e.key)) return;
            e.preventDefault();
            const visible = Array.from(cards).filter((c) => c.offsetParent !== null);
            const idx = visible.indexOf(card);
            if (idx < 0) return;
            const delta = (e.key === 'ArrowRight' || e.key === 'ArrowDown') ? 1 : -1;
            const next = visible[(idx + delta + visible.length) % visible.length];
            next.focus();
        });
    });

    if (resetTheme) {
        resetTheme.addEventListener('click', () => {
            if (typeof window.setTheme === 'function') window.setTheme('system');
            else localStorage.setItem('theme', 'system');
            syncCardSelection();
        });
    }

    window.addEventListener('themechange', syncCardSelection);
    syncCardSelection();

    /* ─── Filter chips ───────────────────────────────────────── */

    let modeFilter = 'all';
    let familyFilter = null;

    function applyFilters() {
        let visibleCount = 0;
        familyGroups.forEach((group) => {
            const fam = group.dataset.family;
            const familyOk = !familyFilter || fam === familyFilter;
            const cardsInGroup = group.querySelectorAll('.appearance-preview-card');
            let groupVisible = 0;
            cardsInGroup.forEach((card) => {
                const m = card.dataset.mode;
                const modeOk = modeFilter === 'all' || m === modeFilter;
                const visible = familyOk && modeOk;
                card.style.display = visible ? '' : 'none';
                if (visible) groupVisible++;
            });
            group.style.display = groupVisible > 0 ? '' : 'none';
            visibleCount += groupVisible;
        });

        // System preview shown when no family filter and (mode is all OR
        // current pref is system).
        if (systemRow) {
            const showSystem = !familyFilter && (modeFilter === 'all' || currentPreference() === 'system');
            systemRow.style.display = showSystem ? '' : 'none';
            if (showSystem) visibleCount++;
        }

        if (emptyState) emptyState.hidden = visibleCount > 0;
    }

    modeChips.forEach((chip) => {
        chip.addEventListener('click', () => {
            modeFilter = chip.dataset.modeChip;
            modeChips.forEach((c) =>
                c.classList.toggle('appearance-chip-active', c === chip));
            applyFilters();
        });
    });

    familyChips.forEach((chip) => {
        chip.addEventListener('click', () => {
            const id = chip.dataset.familyChip;
            familyFilter = (familyFilter === id) ? null : id;
            familyChips.forEach((c) =>
                c.classList.toggle('appearance-chip-active', c.dataset.familyChip === familyFilter));
            applyFilters();
        });
    });

    applyFilters();

    /* ─── Accent override ────────────────────────────────────── */

    function syncAccentSwatches() {
        const current = (preferences.accentOverride || '').toUpperCase();
        accentSwatches.forEach((s) => {
            const active = s.dataset.accentHex.toUpperCase() === current;
            s.classList.toggle('is-active', active);
        });
        if (accentDot) {
            accentDot.style.background = preferences.accentOverride || '';
        }
        if (resetAccent) resetAccent.hidden = !preferences.accentOverride;
        if (accentPreview) {
            const v = preferences.accentOverride
                ? deriveAccentVariants(preferences.accentOverride)
                : null;
            if (v) {
                accentPreview.hidden = false;
                accentPreview.querySelectorAll('.appearance-accent-chip').forEach((chip) => {
                    const variant = chip.dataset.variant;
                    const span = chip.querySelector('span');
                    if (variant === 'subtle') {
                        chip.style.background = v.subtle;
                        chip.style.color = v.base;
                        chip.style.border = `1px solid ${v.base}`;
                        if (span) span.style.color = v.base;
                    } else {
                        chip.style.background = v[variant];
                        chip.style.color = v.textOnAccent;
                        chip.style.border = '';
                        if (span) span.style.color = v.textOnAccent;
                    }
                });
            } else {
                accentPreview.hidden = true;
            }
        }
    }

    function setAccent(hex) {
        preferences.accentOverride = hex;
        applyPreferences(preferences);
        persist(preferences);
        syncAccentSwatches();
    }

    accentSwatches.forEach((sw) => {
        sw.addEventListener('click', () => setAccent(sw.dataset.accentHex));
    });

    if (resetAccent) {
        resetAccent.addEventListener('click', () => setAccent(null));
    }

    if (hexInput && applyBtn) {
        hexInput.addEventListener('input', () => {
            applyBtn.disabled = !hexInput.value.trim();
        });
        hexInput.addEventListener('keydown', (e) => {
            if (e.key === 'Enter') {
                e.preventDefault();
                applyHex();
            }
        });
        applyBtn.addEventListener('click', applyHex);
    }

    function applyHex() {
        if (!hexInput) return;
        const trimmed = hexInput.value.trim();
        if (!trimmed) return;
        const normalised = trimmed.startsWith('#') ? trimmed : `#${trimmed}`;
        if (deriveAccentVariants(normalised)) {
            setAccent(normalised);
            hexInput.value = '';
            if (applyBtn) applyBtn.disabled = true;
        }
    }

    syncAccentSwatches();

    /* ─── Display: density / motion / fontScale ───────────────── */

    function syncSegmented() {
        segmenteds.forEach((seg) => {
            const pref = seg.dataset.pref;
            const current = preferences[pref];
            seg.querySelectorAll('.appearance-segmented-btn').forEach((btn) => {
                const v = btn.dataset.value;
                let parsed = null;
                if (pref === 'fontScale') {
                    parsed = v === '' ? null : parseFloat(v);
                } else {
                    parsed = v === '' ? null : v;
                }
                const isActive =
                    (current === null && parsed === null) ||
                    (current !== null && parsed !== null && current === parsed);
                btn.classList.toggle('is-active', isActive);
            });
        });
    }

    segmenteds.forEach((seg) => {
        const pref = seg.dataset.pref;
        seg.querySelectorAll('.appearance-segmented-btn').forEach((btn) => {
            btn.addEventListener('click', () => {
                const v = btn.dataset.value;
                let next;
                if (v === '') {
                    next = null;
                } else if (pref === 'fontScale') {
                    next = parseFloat(v);
                } else {
                    next = v;
                }
                preferences[pref] = next;
                applyPreferences(preferences);
                persist(preferences);
                syncSegmented();
            });
        });
    });

    syncSegmented();

    /* ─── Reset all ───────────────────────────────────────────── */

    if (resetAll) {
        resetAll.addEventListener('click', () => {
            preferences = Object.assign({}, DEFAULTS);
            applyPreferences(preferences);
            persist(preferences);
            syncAccentSwatches();
            syncSegmented();
        });
    }
})();
